import React from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { ActionButtons } from '../../../common/components';
import { LOADING_STATUS, PRICE_CHANGE_TYPES } from '../../../common/constants';
import { AddonServiceRuleItemsData } from '../../../common/types/rates.types';
import { AccommodationAddOns } from '../../accommodations/accommodationsSlice';

function AddonServiceRulesForm({
  loadingStatus,
  addonsLoadingStatus,
  handleInputChange,
  handleSelectChange,
  onSubmit,
  submitted,
  data,
  accommodationAddons,
  hasWriteAccess,
  isDefaultRate = false,
  handleAddRule,
  handleRemoveRule,
}: {
  loadingStatus: LOADING_STATUS;
  addonsLoadingStatus: LOADING_STATUS;
  handleInputChange: (
    index: string,
    field: string,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  /* TODO Replace any/ CORE-1006 */
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  handleSelectChange: any;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  submitted: boolean;
  data: AddonServiceRuleItemsData;
  accommodationAddons: AccommodationAddOns;
  hasWriteAccess: boolean;
  isDefaultRate?: boolean;
  handleAddRule: (addonId: string, addonName: string) => void;
  handleRemoveRule: (addonId: string) => void;
}) {
  const priceChangeOptions = PRICE_CHANGE_TYPES.map(priceChangeType => {
    return {
      value: priceChangeType.priceChangeType,
      label: priceChangeType.description,
    };
  });

  const findRuleForAddon = (addonDefinitionId: string) => {
    return data.additionalServiceRules.find(
      rule => rule.additionalServiceId === addonDefinitionId,
    );
  };

  return (
    <>
      {(loadingStatus === LOADING_STATUS.LOADING ||
        addonsLoadingStatus === LOADING_STATUS.LOADING) && (
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">Ladataan...</span>
        </Spinner>
      )}
      {(loadingStatus === LOADING_STATUS.IDLE ||
        addonsLoadingStatus === LOADING_STATUS.IDLE) && (
        <>
          {accommodationAddons?.addOns.map(addon => {
            const rule = findRuleForAddon(addon.addOnDefinitionId.toString());
            return (
              <div key={addon.id}>
                {rule ? (
                  <>
                    <hr
                      className="hr-light mt-40 mb-32"
                      style={{ borderWidth: '4px' }}
                    />
                    <Form.Control
                      type="hidden"
                      name={`additionalServiceRules[${rule.additionalServiceId}].additionalServiceId`}
                      value={rule.additionalServiceId}
                    />
                    <h4 className="text-uppercase text-muted mb-2">
                      {rule.additionalServiceName || ''} -{' '}
                      {addon.automatic ? (
                        <span className="text-info">Näkyy varattavissa</span>
                      ) : (
                        <span className="text-danger">Ei näy varattavissa</span>
                      )}
                    </h4>
                    <Form.Group
                      controlId={`isMandatory-${rule.additionalServiceId}`}
                      className="mb-24"
                    >
                      <Form.Label>Pakollinen lisäpalvelu</Form.Label>
                      <Form.Check
                        type="checkbox"
                        name={`additionalServiceRules[${rule.additionalServiceId}].isMandatory`}
                        checked={rule.isMandatory}
                        onChange={handleInputChange(
                          rule.additionalServiceId,
                          'isMandatory',
                        )}
                        disabled={!hasWriteAccess}
                      />
                    </Form.Group>
                    <Form.Group className="mb-24">
                      <Form.Label>Hinnan muutoksen tyyppi</Form.Label>
                      <Select
                        inputId={`priceChangeType-${rule.additionalServiceId}`}
                        name={`additionalServiceRules[${rule.additionalServiceId}].priceChange.priceChangeType`}
                        placeholder="Valitse..."
                        isClearable
                        isSearchable
                        options={priceChangeOptions}
                        onChange={option =>
                          handleSelectChange(
                            option,
                            rule.additionalServiceId,
                            'priceChange.priceChangeType',
                          )
                        }
                        value={priceChangeOptions.find(
                          option =>
                            option.value === rule.priceChange?.priceChangeType,
                        )}
                        classNames={{
                          control: state =>
                            'form-control form-control-react-select',
                        }}
                        isDisabled={!hasWriteAccess}
                      />
                    </Form.Group>

                    {rule.priceChange?.priceChangeType === 1 && (
                      <Form.Group
                        controlId={`difference-${rule.additionalServiceId}`}
                        className="mb-24"
                      >
                        <Form.Label>Kiinteä</Form.Label>
                        <Form.Control
                          type="text"
                          name={`additionalServiceRules[${rule.additionalServiceId}].priceChange.difference`}
                          value={rule.priceChange?.difference || ''}
                          onChange={handleInputChange(
                            rule.additionalServiceId,
                            'priceChange.difference',
                          )}
                          readOnly={!hasWriteAccess}
                        />
                      </Form.Group>
                    )}

                    {rule.priceChange?.priceChangeType === 2 && (
                      <Form.Group
                        controlId={`percent-${rule.additionalServiceId}`}
                        className="mb-24"
                      >
                        <Form.Label>Prosentuaalinen</Form.Label>
                        <Form.Control
                          type="text"
                          name={`additionalServiceRules[${rule.additionalServiceId}].priceChange.percent`}
                          value={rule.priceChange?.percent || ''}
                          onChange={handleInputChange(
                            rule.additionalServiceId,
                            'priceChange.percent',
                          )}
                          readOnly={!hasWriteAccess}
                        />
                      </Form.Group>
                    )}
                    <Form.Group
                      controlId={`isCopyable-${rule.additionalServiceId}`}
                      className="mb-24"
                    >
                      <Form.Label>Kopioitava sääntö</Form.Label>
                      <Form.Check
                        type="checkbox"
                        name={`additionalServiceRules[${rule.additionalServiceId}].isCopyable`}
                        checked={rule.isCopyable || false}
                        onChange={handleInputChange(
                          rule.additionalServiceId,
                          'isCopyable',
                        )}
                        disabled={!hasWriteAccess}
                      />
                    </Form.Group>
                    <Button
                      variant="danger"
                      onClick={() => handleRemoveRule(rule.additionalServiceId)}
                      disabled={!hasWriteAccess}
                    >
                      Poista sääntö
                    </Button>
                  </>
                ) : (
                  <>
                    <hr
                      className="hr-light mt-40 mb-32"
                      style={{ borderWidth: '4px' }}
                    />
                    <h4 className="text-uppercase text-muted mb-2">
                      {addon.name || ''} -{' '}
                      {addon.automatic ? (
                        <span className="text-info">Näkyy varattavissa</span>
                      ) : (
                        <span className="text-danger">Ei näy varattavissa</span>
                      )}
                    </h4>
                    <Button
                      variant="primary"
                      onClick={() =>
                        handleAddRule(
                          addon.addOnDefinitionId.toString(),
                          addon.name,
                        )
                      }
                      disabled={!hasWriteAccess}
                    >
                      Lisää lisäpalvelusääntö
                    </Button>
                  </>
                )}
              </div>
            );
          })}

          <hr className="hr-light mt-40 mb-32" style={{ borderWidth: '4px' }} />

          <ActionButtons
            HandleSave={onSubmit}
            HideSaveButton={!hasWriteAccess}
            HideCancelButton
            DeleteButtonText=""
          />
          {submitted && (
            <div className="mt-2 pt-2">
              <Alert variant="success">Tiedot tallennettu!</Alert>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default AddonServiceRulesForm;
